import Heading from '../../components/shared/heading';
import React, { useContext, useEffect, useState } from 'react';
import EditableField from '../../components/shared/editable-input';
import { RxCross2 } from "react-icons/rx";
import { AccountsContext } from '../../components/context/account-context';
import Loader from '../../components/shared/loader';
import { camelCaseToWords } from '../../utils/structure-words-utils';
import { Country, State, City } from 'country-state-city';
import { updateJob } from '../../services/jobs-service';
import GoogleMapComponent from '../../components/shared/google-map';
import ModalHeading from '../../components/shared/modal-heading';
import Modal from '../../components/shared/modal';
import { MdContentCopy } from 'react-icons/md';
import { FiExternalLink } from 'react-icons/fi';

const EditModal = ({ selectedRow, setSelectedRow, setIsEditModalOpen, setData, globalDataLoading }) => {
    const { accountId } = useContext(AccountsContext);
    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [loadingFields, setLoadingFields] = useState({});
    const [updateStatus, setUpdateStatus] = useState({ message: '', type: '' });
    const [openMapModal, setOpenMapModal] = useState(false);
    const [copied, setCopied] = useState(false);
    const [mapPosition, setMapPosition] = useState(
        selectedRow.location
            ? {
                lat: parseFloat(selectedRow.location.split(",")[0]),
                lng: parseFloat(selectedRow.location.split(",")[1]),
            }
            : { lat: 0, lng: 0 }
    );

    // Clear status messages after 2 seconds
    useEffect(() => {
        let timer;
        if (updateStatus.message && updateStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUpdateStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [updateStatus]);

    // Load countries on component mount
    useEffect(() => {
        const countries = Country.getAllCountries().map((country) => ({
            value: country.name,
            label: country.name,
            isoCode: country.isoCode
        }));
        setCountryOptions([{ value: '', label: 'Select Country' }, ...countries]);
    }, []);

    // Load states when country changes
    useEffect(() => {
        if (selectedRow?.country) {
            const selectedCountry = countryOptions.find(c => c.value === selectedRow.country);
            if (selectedCountry) {
                const states = State.getStatesOfCountry(selectedCountry.isoCode).map((state) => ({
                    value: state.name,
                    label: state.name,
                    isoCode: state.isoCode
                }));
                setStateOptions([{ value: '', label: 'Select State' }, ...states]);
            } else {
                setStateOptions([{ value: '', label: 'Select State' }]);
            }
        } else {
            setStateOptions([{ value: '', label: 'Select State' }]);
        }
    }, [selectedRow?.country, countryOptions]);

    // Load cities when state changes
    useEffect(() => {
        if (selectedRow?.country && selectedRow?.state) {
            const selectedCountry = countryOptions.find(c => c.value === selectedRow.country);
            const selectedState = stateOptions.find(s => s.value === selectedRow.state);

            if (selectedCountry && selectedState) {
                const cities = City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode).map((city) => ({
                    value: city.name,
                    label: city.name,
                }));
                setCityOptions([{ value: '', label: 'Select City' }, ...cities]);
            } else {
                setCityOptions([{ value: '', label: 'Select City' }]);
            }
        } else {
            setCityOptions([{ value: '', label: 'Select City' }]);
        }
    }, [selectedRow?.country, selectedRow?.state, stateOptions]);

    const handleFieldUpdate = async (value, fieldName) => {
        console.log(value, fieldName);
        setLoadingFields(prev => ({ ...prev, [fieldName]: true }));
        setUpdateStatus({ message: '', type: '' });

        const fieldMap = {
            startTime: 'startTime',
            endTime: 'endTime',
            name: 'name',
            location: 'location',
            address: 'address',
            country: 'country',
            state: 'state',
            city: 'city',
            postalCode: 'postalCode',
            type: 'type',
            status: 'status',
            note: 'note'
        };

        const payload = {};
        if (fieldName === 'startTime' || fieldName === 'endTime') {
            payload[fieldMap[fieldName]] = new Date(value).toISOString();
        } else {
            payload[fieldMap[fieldName]] = value;
        }

        try {
            const response = await updateJob(accountId, selectedRow.jobId, payload);
            console.log(response);

            if (response.success) {
                const updatedRow = { ...selectedRow };

                switch (fieldName) {
                    case 'startTime':
                        updatedRow.startTime = payload.startTime;
                        break;
                    case 'endTime':
                        updatedRow.endTime = payload.endTime;
                        break;
                    default:
                        updatedRow[fieldName] = value;
                }

                setSelectedRow(updatedRow);
                setData(prevData =>
                    prevData.map(row =>
                        row.jobId === selectedRow.jobId ? updatedRow : row
                    )
                );
                setUpdateStatus({
                    message: `${labelMap[fieldName] || fieldName} updated successfully!`,
                    type: 'success'
                });
            } else {
                setUpdateStatus({
                    message: response.data?.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                    type: 'error'
                });
            }
        } catch (error) {
            setUpdateStatus({
                message: `Failed to update ${labelMap[fieldName] || fieldName}`,
                type: 'error'
            });
        } finally {
            setLoadingFields(prev => ({ ...prev, [fieldName]: false }));
        }
    };


    const handleSelectChange = async (option, fieldName) => {
        if (!selectedRow) return;

        console.log(option)

        setLoadingFields((prev) => ({ ...prev, [fieldName]: true }));
        setUpdateStatus({ message: '', type: '' });

        const newValue = option ? option : '';
        let payload = { [fieldName]: newValue };

        if (fieldName === 'country') {
            payload = { ...payload, state: '', city: '' };
        } else if (fieldName === 'state') {
            payload = { [fieldName]: newValue }; // Only send state in payload
        }

        console.log(payload)

        try {
            const response = await updateJob(accountId, selectedRow.jobId, payload);
            if (response.success) {
                // Map API response to match the selectedRow structure
                const updatedCountry = countryOptions.find(c => c.value === response.data.data.country)?.value || '';
                const updatedState = stateOptions.find(s => s.value === response.data.data.state)?.value || '';
                const updatedCity = fieldName === 'state' ? '' : response.data.data.city || ''; // Reset city only if state is updated

                const updatedRow = {
                    ...selectedRow,
                    country: updatedCountry,
                    state: updatedState,
                    city: updatedCity
                };

                setSelectedRow(updatedRow);
                setData((prevData) =>
                    prevData.map((row) =>
                        row.jobId === selectedRow.jobId ? updatedRow : row
                    )
                );
                setUpdateStatus({
                    message: `${labelMap[fieldName] || fieldName} updated successfully!`,
                    type: 'success'
                });
            } else {
                setUpdateStatus({
                    message: response.data?.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                    type: 'error'
                });
            }
        } catch (error) {
            setUpdateStatus({
                message: `Failed to update ${labelMap[fieldName] || fieldName}`,
                type: 'error'
            });
        } finally {
            setLoadingFields((prev) => ({ ...prev, [fieldName]: false }));
        }
    };

    const typesOption = [
        { value: '', label: 'Select Type' },
        { value: 'dispatch', label: 'Dispatch' },
        { value: 'project', label: 'Project' },
    ];

    const labelMap = {
        type: 'Type',
        name: 'Name',
        location: 'Location',
        address: 'Address',
        country: 'Country',
        state: 'State',
        city: 'City',
        postalCode: 'Postal Code',
        startTime: 'Start Time',
        endTime: 'End Time',
        status: 'Status',
        note: 'Note'
    };

    // Handle location change from map
    const handleLocationChange = (newLocationString) => {
        // newLocationString is in "lat,lng" format
        const [lat, lng] = newLocationString.split(",").map(parseFloat);
        if (isNaN(lat) || isNaN(lng)) {
            console.error('Invalid location format:', newLocationString);
            return;
        }
        const newLat = lat.toFixed(6);
        const newLng = lng.toFixed(6);
        const formattedLocation = `${newLat},${newLng}`;
        setMapPosition({ lat: parseFloat(newLat), lng: parseFloat(newLng) });
        handleFieldUpdate(formattedLocation, 'location');
        // setOpenMapModal(false); // Close modal after updating
    };

    //  Handle copy location
    const handleCopyLocation = () => {
        if (selectedRow?.location) {
            navigator.clipboard.writeText(selectedRow.location).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 3000); // hide after 3s
            });
        }
    };

    const handleLocationEditClick = () => {
        setOpenMapModal(true);
    };

    return (
        <div className="space-y-3 px-4 pt-3 pb-4 relative">
            <Heading className="text-left mb-3">Job Information</Heading>

            <button
                className='hover:scale-105 absolute -top-3 -right-2 bg-white border p-2 rounded-full'
                onClick={() => setIsEditModalOpen(false)}>
                <RxCross2 className='text-2xl' />
            </button>

            {updateStatus.message && (
                <div className={`px-4 py-2 text-sm ${updateStatus.type === 'success' ? 'text-green-600 bg-green-100' :
                    updateStatus.type === 'error' ? 'text-red-600 bg-red-100' :
                        'text-blue-600 bg-blue-100'} rounded-md mx-4`}>
                    {updateStatus.message}
                </div>
            )}
            {
                globalDataLoading ?
                    <div className='py-40 flex justify-center w-full'>
                        <Loader />
                    </div>
                    :
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">

                        <EditableField
                            label="Start Time"
                            name="startTime"
                            value={selectedRow.startTime || ''}
                            onChange={(value) => handleFieldUpdate(value, 'startTime')}
                            type="datetime-local"
                            isLoading={loadingFields.startTime}
                            isEditable={false}
                        />

                        <EditableField
                            label="End Time"
                            name="endTime"
                            value={selectedRow.endTime || ''}
                            onChange={(value) => handleFieldUpdate(value, 'endTime')}
                            type="datetime-local"
                            isLoading={loadingFields.endTime}
                        />



                        <EditableField
                            label="Type"
                            name="type"
                            value={camelCaseToWords(selectedRow.type) || ''}
                            onChange={(option) => handleSelectChange(option, 'type')}
                            type="select"
                            options={typesOption}
                            isLoading={loadingFields.type}
                            isEditable={false}
                        />

                        <EditableField
                            label="Name"
                            name="name"
                            value={selectedRow.name || ''}
                            onChange={handleFieldUpdate}
                            isEditable={true}
                            isLoading={loadingFields.name}
                        />

                        <div className='flex items-center w-full gap-1'>
                            <EditableField
                                label="Location"
                                name="location"
                                value={
                                    selectedRow.location
                                        ? (() => {
                                            const [lat, lng] = selectedRow.location.split(",");
                                            const formattedLat = parseFloat(lat).toFixed(3);
                                            const formattedLng = parseFloat(lng).toFixed(3);
                                            return `${formattedLat}, ${formattedLng}`;
                                        })()
                                        : ""
                                }
                                onChange={(value) => handleFieldUpdate(value, "location")}
                                type="text"
                                isLoading={loadingFields.location}
                                onEditClick={handleLocationEditClick}
                            />
                            <div className='flex items-center gap-2 w-16  mt-4  relative'>
                                {copied && (
                                    <p className="text-[10px] absolute right-3 -top-4 text-green-500">
                                        Copied!
                                    </p>
                                )}
                                <button
                                    onClick={() => {
                                        if (selectedRow.location) {
                                            const [lat, lng] = selectedRow.location.split(",");
                                            const url = `https://www.google.com/maps?q=${lat},${lng}`;
                                            window.open(url, "_blank");
                                        }
                                    }}
                                >
                                    <FiExternalLink className='text-blue-500 hover:text-blue-600' />
                                </button>
                                <button onClick={handleCopyLocation}>
                                    <MdContentCopy className='text-green-500 hover:text-green-600' />
                                </button>
                            </div>

                            <Modal
                                isOpen={openMapModal}
                                onClose={() => setOpenMapModal(false)}
                            >
                                <div className='min-h-[350px]'>
                                    <div className='w-full h-5 mb-5 relative'>
                                        <ModalHeading>Select Location</ModalHeading>
                                        <button
                                            className="hover:scale-105 absolute -top-2 -right-2 bg-white border p-2 rounded-full"
                                            onClick={() => setOpenMapModal(false)}
                                        >
                                            <RxCross2 className="text-xl" />
                                        </button>
                                    </div>
                                    <GoogleMapComponent
                                        isVisible={openMapModal}
                                        initialLocation={mapPosition}
                                        onLocationChange={handleLocationChange}
                                    />
                                </div>
                            </Modal>
                        </div>

                        <EditableField
                            label="Address"
                            name="address"
                            value={selectedRow.address || ''}
                            onChange={handleFieldUpdate}
                            isEditable={true}
                            isLoading={loadingFields.address}
                        />

                        <EditableField
                            label="Country"
                            name="country"
                            value={selectedRow.country || ''}
                            onChange={(option) => handleSelectChange(option, 'country')}
                            type="select"
                            options={countryOptions}
                            isLoading={loadingFields.country}
                        />

                        <EditableField
                            label="State"
                            name="state"
                            value={selectedRow.state || ''}
                            onChange={(option) => handleSelectChange(option, 'state')}
                            type="select"
                            options={stateOptions}
                            isDisabled={!selectedRow.country}
                            isLoading={loadingFields.state}
                        />

                        <EditableField
                            label="City"
                            name="city"
                            value={selectedRow.city || ''}
                            onChange={(option) => handleSelectChange(option, 'city')}
                            type="select"
                            options={cityOptions}
                            isDisabled={!selectedRow.state}
                            isLoading={loadingFields.city}
                        />

                        <EditableField
                            label="Postal Code"
                            name="postalCode"
                            value={selectedRow.postalCode || ''}
                            onChange={handleFieldUpdate}
                            isEditable={true}
                            isLoading={loadingFields.postalCode}
                        />


                        <div>
                            <EditableField
                                label="Status"
                                name="status"
                                value={camelCaseToWords(selectedRow.status) || ''}
                                onChange={(option) => handleSelectChange(option, 'status')}
                                type="select"
                                isEditable={false}
                                isLoading={loadingFields.status}
                            />
                        </div>

                        <EditableField
                            label="Note"
                            name="note"
                            type='textarea'
                            rows={2}
                            value={selectedRow?.note || ''}
                            onChange={handleFieldUpdate}
                            isLoading={loadingFields.note}
                        />
                    </div>
            }
        </div>
    );
};

export default EditModal;